import { Cancel, CleaningServices, Save, Search } from '@mui/icons-material';
import { Box, Button, Chip, InputAdornment, MenuItem, Paper, Select, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import React from 'react'
import { useEffect } from 'react';
import { useState } from 'react';
import CustomSnackBar from '../../components/CustomSnackBar';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { normalizeInvoiceLineFormat } from '../../components/InvoiceStepper/invoiceReconciliation';


const formatCurrency = (value) =>
  new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(value);

const sumDetailsTotal = (details) => {
  const totalCents = (details || []).reduce((sum, item) => {
    const qty = Number(item?.quantity) || 0;
    const priceNum = Number(item?.price);
    const priceCents = Number.isFinite(priceNum) ? Math.round(priceNum * 100) : 0;
    return sum + qty * priceCents;
  }, 0);
  return (totalCents / 100).toFixed(2);
};

const buildBonusKey = (bonus, index) => {
  const id = bonus?.id ?? bonus?.order_bonus_id ?? bonus?.orderBonusId ?? null;
  const productId = bonus?.product?.id ?? bonus?.product_id ?? null;
  const detailId =
    bonus?.order_detail_id ?? bonus?.order_detail ?? bonus?.detail_id ?? bonus?.orderDetailId ?? null;
  if (id != null) {
    return `id-${id}`;
  }
  if (productId != null || detailId != null) {
    return `p-${productId ?? 'x'}-d-${detailId ?? 'x'}-i-${index}`;
  }
  return `idx-${index}`;
};

const sanitizeIntegerInput = (value) => String(value ?? '').replace(/\D+/g, '');

const handleNumericWheel = (event) => {
  event.preventDefault();
  event.currentTarget.blur();
};

export default function AddInvoiceDetail({ setOpen, orderUnified, setInvoice, invoice, detailLoading = false }) {
  const getFormatLabel = (fmt) => {
    if (fmt == null) return '';
    if (typeof fmt === 'string' || typeof fmt === 'number') return String(fmt);
    if (typeof fmt === 'object') return fmt.name || fmt.prformat || fmt.value || '';
    return String(fmt);
  };

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [productsState, setProductsState] = useState([]);
  const [bonusesState, setBonusesState] = useState([]);
  const [modifiedProductsState, setModifiedProductsState] = useState({});
  const [modifiedBonusesState, setModifiedBonusesState] = useState({});

  const products = productsState;
  const bonuses = bonusesState;
  const modifiedProducts = modifiedProductsState;
  const modifiedBonuses = modifiedBonusesState;

  const setProducts = useCallback((updater) => {
    setProductsState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);
  const setBonuses = useCallback((updater) => {
    setBonusesState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);
  const setModifiedProducts = useCallback((updater) => {
    setModifiedProductsState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);
  const setModifiedBonuses = useCallback((updater) => {
    setModifiedBonusesState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const getProductId = useCallback((product) => (
    product?.product?.id ?? product?.product_id ?? product?.id ?? null
  ), []);

  const getFormatSignature = useCallback((item) => {
    const formatId = item?.prformat_id ?? item?.format_id ?? item?.prformat?.id ?? item?.product?.prformat?.id ?? null;
    if (formatId !== null && formatId !== undefined && formatId !== '') return `id:${formatId}`;
    const formatName = String(
      item?.format_name
      ?? item?.prformat_name
      ?? item?.prformat?.name
      ?? item?.prformat?.prformat
      ?? item?.prformat?.value
      ?? item?.prformat
      ?? item?.product?.prformat?.name
      ?? ''
    ).trim().toLowerCase();
    return formatName ? `name:${formatName}` : 'none';
  }, []);

  const getProductRowKey = useCallback((product) => {
    const productId = getProductId(product);
    if (!productId) return null;
    return `${productId}::${getFormatSignature(product)}`;
  }, [getFormatSignature, getProductId]);

  const isSameProductAndFormat = useCallback((detailOrInvoiceRow, productRow) => {
    const leftProductId = detailOrInvoiceRow?.product_id ?? detailOrInvoiceRow?.product?.id ?? detailOrInvoiceRow?.product ?? null;
    const rightProductId = getProductId(productRow);
    if (String(leftProductId ?? '') !== String(rightProductId ?? '')) return false;
    return getFormatSignature(detailOrInvoiceRow) === getFormatSignature(productRow);
  }, [getFormatSignature, getProductId]);

  useEffect(() => {
    setProducts([]);
    setBonuses([]);
    setModifiedProducts({});
    setModifiedBonuses({});
    setSearchTerm('');

    const unifiedDetails = orderUnified?.order_details || orderUnified?.orderDetails || [];
    const unifiedBonuses = orderUnified?.order_bonus || orderUnified?.orderBonus || [];
    if (!Array.isArray(unifiedDetails) || unifiedDetails.length === 0) return;

    const safeNumber = (value) => {
      const num = Number(value);
      return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
    };
    const invoiceDetails = invoice?.invoice_detail || [];
    const invoiceBonuses = invoice?.invoice_bonus || [];

    const productDetails = unifiedDetails.map((item) => {
      const normalizedFormat = normalizeInvoiceLineFormat(item);
      const matchingDetails = invoiceDetails.filter((d) => isSameProductAndFormat(d, item));
      const referenceDetail = matchingDetails[0] || null;
      const quantityFromInvoice = matchingDetails.reduce(
        (sum, detail) => sum + (Number(detail?.quantity) || 0),
        0
      );
      const quantity = quantityFromInvoice > 0 ? quantityFromInvoice : item.quantity;
      const selectedPrice = Number(referenceDetail?.price ?? referenceDetail?.finalPrice);
      const selectedPriceType = referenceDetail
        ? (selectedPrice === Number(item.bonusPrice) && Number(item.bonusPrice) > 0 ? 'bonusPrice' :
          selectedPrice === Number(item.listPrice) ? 'listPrice' : 'finalPrice')
        : 'listPrice';

      return {
        ...item,
        code: item.product.code,
        checked: true,
        originalListPrice: safeNumber(item.listPrice || 0),
        originalFinalPrice: safeNumber(item.finalPrice || 0),
        listPrice: safeNumber(item.listPrice),
        bonusPrice: safeNumber(item.bonusPrice || 0),
        finalPrice: safeNumber(item.finalPrice),
        selectedPriceType,
        product: item.product,
        prformat: item.prformat || null,
        prformat_id: normalizedFormat.prformat_id,
        format_id: normalizedFormat.format_id,
        format_name: normalizedFormat.format_name,
        quantity,
        originalQuantity: item.quantity,
        old_taken: item.taken_quantity || 0,
        taken_quantity: item.taken_quantity || 0,
        remaining_quantity: item.remaining_quantity || 0,
        source_orders: item.source_orders || [],
      };
    });

    const bonusDetails = unifiedBonuses.map((bonus, idx) => {
      const normalizedFormat = normalizeInvoiceLineFormat(bonus);
      const orderBonusIds = Array.isArray(bonus?.order_bonus_ids)
        ? bonus.order_bonus_ids.filter((id) => id != null)
        : [];
      const fallbackOrderBonusId = bonus?.order_bonus_id ?? bonus?.id ?? orderBonusIds[0] ?? null;
      const bonusKey = buildBonusKey(bonus, idx);
      const relatedInvoiceBonuses = invoiceBonuses.filter((b) => {
        const bonusOrderId = b?.order_bonus_id ?? b?.order_bonus?.id ?? null;
        if (bonusOrderId != null && orderBonusIds.length > 0) {
          return orderBonusIds.includes(bonusOrderId);
        }
        return (
          Number(b?.product_id ?? b?.product?.id) === Number(bonus?.product?.id)
          && Number(bonusOrderId ?? fallbackOrderBonusId ?? -1) === Number(fallbackOrderBonusId ?? -1)
        );
      });
      const selectedQty = relatedInvoiceBonuses.reduce(
        (sum, item) => sum + (Number(item?.quantity) || 0),
        0
      );
      const oldTaken = Number(bonus?.taken_quantity || 0);
      const remainingQty = Number(bonus?.remaining_quantity ?? bonus?.quantity ?? 0);
      const defaultQty = selectedQty > 0 ? selectedQty : remainingQty;
      const safeQty = Math.max(0, Math.min(defaultQty, remainingQty));

      return {
        ...bonus,
        order_bonus_id: fallbackOrderBonusId,
        order_bonus_ids: orderBonusIds,
        bonusKey,
        originalQuantity: bonus.quantity,
        quantity: safeQty,
        old_taken: oldTaken,
        taken_quantity: oldTaken + safeQty,
        remaining_quantity: Math.max(remainingQty - safeQty, 0),
        checked: safeQty > 0,
        prformat: relatedInvoiceBonuses[0]?.prformat ?? bonus.prformat ?? null,
        prformat_id:
          (relatedInvoiceBonuses[0]?.prformat?.id)
          || normalizedFormat.prformat_id,
        format_id: normalizedFormat.format_id,
        format_name: normalizedFormat.format_name,
        dueDate: relatedInvoiceBonuses[0]?.dueDate ?? null,
        source_orders: bonus?.source_orders || [],
      };
    });

    const initialModified = {};
    productDetails.forEach((product) => {
      const matchingDetails = invoiceDetails.filter((detail) => isSameProductAndFormat(detail, product));
      if (matchingDetails.length === 0) return;
      const productRowKey = getProductRowKey(product);
      if (!productRowKey) return;
      const totalQuantity = matchingDetails.reduce(
        (sum, detail) => sum + (Number(detail?.quantity) || 0),
        0
      );
      const referenceDetail = matchingDetails[0];
      const detailPrice = Number(referenceDetail?.price ?? referenceDetail?.finalPrice ?? product.listPrice);

      initialModified[productRowKey] = {
        ...product,
        quantity: totalQuantity,
        taken_quantity: totalQuantity + (product.old_taken || 0),
        remaining_quantity: product.originalQuantity - totalQuantity,
        price: detailPrice,
        selectedPriceType:
          detailPrice === Number(product.bonusPrice) && Number(product.bonusPrice) > 0 ? 'bonusPrice' :
            detailPrice === Number(product.listPrice) ? 'listPrice' : 'finalPrice',
      };
    });

    const initialModifiedBonuses = {};
    bonusDetails.forEach((bonus, idx) => {
      const bonusKey = bonus.bonusKey || buildBonusKey(bonus, idx);
      initialModifiedBonuses[bonusKey] = {
        ...bonus,
        quantity: bonus.quantity,
        taken_quantity: bonus.taken_quantity,
        remaining_quantity: bonus.remaining_quantity,
        originalQuantity: bonus.originalQuantity,
        old_taken: bonus.old_taken
      };
    });

    setProducts(productDetails);
    setBonuses(bonusDetails);
    setModifiedProducts(initialModified);
    setModifiedBonuses(initialModifiedBonuses);
  }, [orderUnified, invoice?.invoice_detail, invoice?.invoice_bonus, setBonuses, setModifiedBonuses, setModifiedProducts, setProducts, isSameProductAndFormat, getProductRowKey]);



  // useEffect(() => {
  //   if (orderUnified && orderUnified.length > 0 && products.length === 0) {

  //     const initialProducts = orderUnified.map(item => {
  //       const existingDetail = invoice.invoice_detail.find(
  //         d => d.product_id === item.product.id
  //       );

  //       const quantity = existingDetail ? existingDetail.quantity : item.quantity;

  //       const selectedPriceType = existingDetail
  //         ? (existingDetail.price === item.bonusPrice && item.bonusPrice > 0 ? 'bonusPrice' :
  //           existingDetail.price === item.listPrice ? 'listPrice' : 'finalPrice')
  //         : 'listPrice';

  //       const processedBonuses = (item.order_bonuses || []).map(bonus => {
  //         // Buscar exclusivamente en invoice_detail.bonuses
  //         const detailBonus = existingDetail?.bonuses?.find(b => b.product_id === bonus.product?.id);

  //         return {
  //           ...bonus,
  //           originalQuantity: bonus.quantity,
  //           quantity: detailBonus ? detailBonus.quantity : 0,
  //           old_taken: bonus.taken_quantity || 0,
  //           taken_quantity: detailBonus ? detailBonus.quantity : bonus.taken_quantity,
  //           remaining_quantity: detailBonus
  //             ? bonus.quantity - detailBonus.quantity
  //             : bonus.remaining_quantity,
  //           // Campos adicionales
  //           checked: detailBonus?.checked ?? true,
  //           prformat: detailBonus?.prformat ?? bonus.prformat,
  //           dueDate: detailBonus?.dueDate ?? null
  //         };
  //       });

  //       return {
  //         ...item,
  //         prformat: item.prformat,
  //         code: item.code,
  //         checked: true,
  //         originalListPrice: getSafeNumber(item.listPrice || 0),
  //         originalFinalPrice: getSafeNumber(item.finalPrice || 0),
  //         listPrice: getSafeNumber(item.listPrice),
  //         bonusPrice: getSafeNumber(item.bonusPrice || 0),
  //         finalPrice: getSafeNumber(item.finalPrice),
  //         selectedPriceType,
  //         order_bonuses: processedBonuses,
  //         product: item.product,
  //         quantity,
  //         originalQuantity: item.quantity,
  //         old_taken: item.taken_quantity,
  //         taken_quantity: item.taken_quantity || 0,
  //         remaining_quantity: item.remaining_quantity || 0,
  //         source_orders: item.source_orders || [],
  //       };
  //     });

  //     setProducts(initialProducts);
  //     console.log('products del useEffect: ', products)

  //     // Solo pre-cargamos modifiedProducts si hay invoice_detail
  //     const initialModified = {};
  //     invoice.invoice_detail.forEach(detail => {
  //       const product = orderUnified.find(p => p.product.id === detail.product_id);
  //       if (product) {
  //         initialModified[detail.product_id] = {
  //           ...product,
  //           quantity: detail.quantity,
  //           taken_quantity: detail.quantity + detail.old_taken,
  //           remaining_quantity: product.remaining_quantity - detail.quantity,
  //           price: detail.price,
  //           selectedPriceType:
  //             detail.price === product.bonusPrice && product.bonusPrice > 0 ? 'bonusPrice' :
  //               detail.price === product.listPrice ? 'listPrice' : 'finalPrice',
  //           order_bonuses: product.order_bonuses?.map(bonus => {
  //             const detailBonus = detail.bonuses?.find(b => b.product_id === bonus.product?.id);
  //             const bonusFromOrder = product.order_bonuses?.find(b => b.product?.id === bonus.product?.id);
  //             const oldTaken = bonusFromOrder?.taken_quantity || 0;
  //             return {
  //               ...bonus,
  //               originalQuantity: bonus.quantity,
  //               quantity: detailBonus ? detailBonus.quantity : 0,
  //               old_taken: oldTaken,
  //               taken_quantity: detailBonus ?
  //                 (detailBonus.quantity + oldTaken) :
  //                 (bonus.taken_quantity || 0),
  //               remaining_quantity: detailBonus ?
  //                 bonus.quantity - (detailBonus.quantity + oldTaken) :
  //                 bonus.remaining_quantity,
  //               checked: detailBonus?.checked ?? true,
  //               prformat: detailBonus?.prformat ?? bonus.prformat,
  //               dueDate: detailBonus?.dueDate ?? null
  //             };
  //           }) || []
  //         };
  //       }
  //     });

  //     setModifiedProducts(initialModified);
  //     console.log('modified del useEffect: ', modifiedProducts)
  //   }
  // }, [orderUnified, invoice]);

  // useEffect(() => {
  //   console.log('modified productos: ', modifiedProducts)
  // }, [modifiedProducts])

  // const handleSave = () => {
  //   try {
  //     setLoading(true);

  //     // 1. Filtrar solo productos con quantity > 0
  //     const validModifiedProducts = Object.values(modifiedProducts)
  //       .filter(item => item.quantity > 0);

  //     // 2. Procesar invoice_detail
  //     const updatedDetails = invoice.invoice_detail
  //       .map(detail => {
  //         const modified = validModifiedProducts.find(p => p.product.id === detail.product);
  //         if (!modified) return null; // Eliminar si no esta en modificados y quantity = 0

  //         // Procesar bonos (solo los que tienen quantity > 0)
  //         const validBonuses = modified.order_bonuses
  //           ?.filter(bonus => bonus.quantity > 0)
  //           ?.map(bonus => ({
  //             product_id: bonus.product?.id || bonus.product.id,
  //             description: bonus.product?.description || bonus.description || '',
  //             code: bonus.product?.code || bonus.code || '',
  //             quantity: bonus.quantity,
  //             prformat: bonus.prformat || '',
  //             quantityRe: bonus.quantity,
  //             checked: true,
  //             dueDate: null,
  //             old_taken: bonus.old_taken || 0
  //           })) || [];

  //         return {
  //           ...detail,
  //           quantity: modified.quantity,
  //           price: modified.price || modified.listPrice,
  //           bonuses: validBonuses.length > 0 ? validBonuses : undefined
  //         };
  //       })
  //       .filter(item => item !== null); // Eliminar nulos

  //     // 3. Agregar nuevos productos (solo si quantity > 0)
  //     validModifiedProducts.forEach(product => {
  //       if (!updatedDetails.some(d => d.product === product.product.id)) {
  //         const validBonuses = product.order_bonuses
  //           ?.filter(bonus => bonus.quantity > 0)
  //           ?.map(bonus => ({
  //             product_id: bonus.product?.id || bonus.product.id,
  //             description: bonus.product?.description || bonus.description || '',
  //             code: bonus.product?.code || bonus.code || '',
  //             quantity: bonus.quantity,
  //             prformat: bonus.prformat || '',
  //             quantityRe: bonus.quantity,
  //             checked: true,
  //             dueDate: null,
  //             old_taken: bonus.old_taken || 0
  //           })) || [];

  //         updatedDetails.push({
  //           product_id: product.product.id,
  //           quantity: product.quantity,
  //           quantityRe: product.quantity,
  //           checked: true,
  //           code: product.product?.code || product.code || '',
  //           description: product.product.description,
  //           prformat: product.prformat,
  //           old_taken: product.old_taken || 0,
  //           price: product.price || product.listPrice,
  //           dueDate: new Date().toISOString().split('T')[0],
  //           source_orders: product.source_orders?.map(source => ({
  //             order_id: source.order_id,
  //             detail_id: source.detail_id,
  //             taken_quantity: product.quantity
  //           })) || [],
  //           bonuses: validBonuses.length > 0 ? validBonuses : []
  //         });
  //       }
  //     });

  //     setInvoice(prev => ({
  //       ...prev,
  //       invoice_detail: updatedDetails,
  //       total: updatedDetails.reduce((sum, item) => sum + (item.quantity * item.price), 0)
  //     }));

  //     // Limpiar y cerrar
  //     setModifiedProducts({});
  //     setSnack({ open: true, message: `Detalle guardado`, severity: 'success' });
  //     setTimeout(() => setOpen(false), 800);

  //   } catch (error) {
  //     console.error("Error al guardar:", error);
  //     setSnack({ open: true, message: error.response?.data?.message || 'Error al guardar', severity: 'error' });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSave = () => {
    try {
      setLoading(true);

      const toInt = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return 0;
        return Math.max(0, Math.floor(n));
      };
      const todayISO = new Date().toISOString().split('T')[0];

      const allocateAcrossSources = (requested, sources, idField) => {
        let pending = toInt(requested);
        const allocations = [];
        const sourceList = Array.isArray(sources) ? sources : [];

        for (const src of sourceList) {
          if (pending <= 0) break;
          const sourceId = src?.[idField];
          if (sourceId == null) continue;
          const available = toInt(src?.remaining_quantity ?? src?.original_quantity ?? src?.quantity ?? 0);
          if (available <= 0) continue;
          const take = Math.min(pending, available);
          if (take <= 0) continue;
          allocations.push({ source: src, quantity: take });
          pending -= take;
        }

        return { allocations, pending };
      };

      const validModifiedProducts = Object.values(modifiedProducts)
        .filter((item) => toInt(item.quantity) > 0);

      const updatedDetails = [];
      validModifiedProducts.forEach((product) => {
        const productObj = product?.product;
        if (!productObj?.id) return;

        const requestedQty = toInt(product?.quantity);
        const normalizedPrice = Number(product?.price ?? product?.listPrice ?? 0) || 0;
        const prformat = product?.prformat || null;
        const normalizedFormat = normalizeInvoiceLineFormat(product);
        const sourceOrders = Array.isArray(product?.source_orders) ? product.source_orders : [];
        const { allocations, pending } = allocateAcrossSources(requestedQty, sourceOrders, 'detail_id');
        const clientRowId = product?.clientRowId || getProductRowKey(product) || `p-${productObj.id}`;

        updatedDetails.push(normalizeInvoiceLineFormat({
          clientRowId,
          product_id: productObj.id,
          product: productObj,
          supplier_id: product?.supplier_id ?? productObj?.supplier_id ?? productObj?.supplier?.id ?? invoice?.supplier_id ?? null,
          quantity: requestedQty,
          quantityRe: requestedQty,
          checked: true,
          code: productObj.code,
          description: productObj.description,
          prformat,
          prformat_id: normalizedFormat.prformat_id,
          format_id: normalizedFormat.format_id,
          format_name: normalizedFormat.format_name,
          old_taken: product?.old_taken || 0,
          price: normalizedPrice,
          dueDate: todayISO,
          source_orders: allocations.map(({ source, quantity: allocatedQty }) => ({
            order_id: source.order_id,
            detail_id: source.detail_id,
            taken_quantity: allocatedQty,
          })),
          unassigned_quantity: pending,
        }));
      });

      const updatedBonuses = Object.values(modifiedBonuses || {})
        .filter((bonus) => toInt(bonus?.quantity) > 0)
        .map((bonus) => {
          const productId = bonus.product?.id || bonus.product_id;
          if (!productId) return null;

          const requestedQty = toInt(bonus.quantity);
          const normalizedFormat = normalizeInvoiceLineFormat(bonus);
          const sourceOrders = Array.isArray(bonus?.source_orders) ? bonus.source_orders : [];
          const validSources = sourceOrders.filter((src) => src?.order_bonus_id != null);
          const { allocations, pending } = allocateAcrossSources(requestedQty, validSources, 'order_bonus_id');

          return normalizeInvoiceLineFormat({
            clientRowId: bonus.bonusKey || `b-${productId}`,
            product_id: productId,
            product: bonus.product,
            supplier_id: bonus?.supplier_id ?? bonus?.product?.supplier_id ?? bonus?.product?.supplier?.id ?? invoice?.supplier_id ?? null,
            order_bonus_id: bonus.order_bonus_id ?? bonus.id ?? null,
            quantity: requestedQty,
            quantityRe: requestedQty,
            checked: bonus.checked ?? true,
            code: bonus.product?.code || bonus.code || '',
            description: bonus.product?.description || bonus.description || '',
            prformat: bonus.prformat || '',
            prformat_id: normalizedFormat.prformat_id,
            format_id: normalizedFormat.format_id,
            format_name: normalizedFormat.format_name,
            dueDate: bonus.dueDate || null,
            old_taken: bonus.old_taken || 0,
            is_manual: allocations.length === 0,
            source_orders: allocations.map(({ source, quantity: allocatedQty }) => ({
              order_id: source.order_id,
              order_bonus_id: source.order_bonus_id,
              taken_quantity: allocatedQty,
            })),
            unassigned_quantity: pending,
          });
        })
        .filter(Boolean);

      const newInvoice = {
        ...invoice,
        invoice_detail: updatedDetails,
        invoice_bonus: updatedBonuses,
        total: sumDetailsTotal(updatedDetails),
      };

      setInvoice(newInvoice);
      setModifiedProducts({});
      setModifiedBonuses({});
      setSnack({ open: true, message: 'Detalle guardado', severity: 'success' });
      setTimeout(() => setOpen(false), 800);

    } catch (error) {
      console.error('Error al guardar:', error);
      const message = error?.response?.data?.message || error?.message || 'Error al guardar';
      setSnack({ open: true, message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
  const handleBonusChange = useCallback((bonusKey, bonusIndex, field, value) => {
    const fallbackBonus = bonuses[bonusIndex];
    const resolvedKey = bonusKey ||
      fallbackBonus?.bonusKey ||
      (fallbackBonus ? buildBonusKey(fallbackBonus, bonusIndex) : null);
    if (!resolvedKey) return;
    if (value === '') {
      setModifiedBonuses(prev => ({
        ...prev,
        [resolvedKey]: {
          ...(prev[resolvedKey] || {}),
          [field]: ''
        }
      }));
      return;
    }

    if (!/^\d+$/.test(String(value))) return;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return;

    const originalBonus = bonuses.find(b => b.bonusKey === resolvedKey) || fallbackBonus;
    if (!originalBonus) return;

    const backendOriginal = originalBonus.originalQuantity || originalBonus.quantity;
    const backendOldTaken = originalBonus.old_taken || 0;

    // Evitar que se exceda de lo permitido
    const maxQuantity = backendOriginal - backendOldTaken;
    if (field === 'quantity' && numericValue > maxQuantity) return;

    const newQuantity = field === 'quantity' ? numericValue : originalBonus.quantity;
    const newTaken = backendOldTaken + newQuantity;
    const newRemaining = backendOriginal - newTaken;

    setModifiedBonuses(prev => ({
      ...prev,
      [resolvedKey]: {
        ...(prev[resolvedKey] || originalBonus),
        [field]: newQuantity,
        taken_quantity: newTaken,
        remaining_quantity: newRemaining,
        originalQuantity: backendOriginal,
        old_taken: backendOldTaken
      }
    }));
  }, [bonuses]);


  const getBonusDisplayValue = (bonus, field) => {
    if (!bonus) return field === 'quantity' ? '' : 0;

    const bonusKey = bonus.bonusKey || (bonus.id != null ? `id-${bonus.id}` : null);
    const modifiedBonus = bonusKey ? modifiedBonuses[bonusKey] : undefined;
    const legacyModified = bonus?.id != null ? modifiedBonuses[bonus.id] : undefined;
    const resolvedModified = modifiedBonus || legacyModified;

    switch (field) {
      case 'quantity':
        return resolvedModified?.quantity ?? bonus.quantity ?? '';
      case 'taken_quantity':
        return resolvedModified?.taken_quantity ?? bonus.taken_quantity ?? 0;
      case 'remaining_quantity':
        return resolvedModified?.remaining_quantity ??
          bonus.remaining_quantity ?? 0;
      case 'originalQuantity':
        return bonus.originalQuantity ?? bonus.quantity ?? 0;
      default:
        return resolvedModified?.[field] ?? bonus[field] ?? '';
    }
  };


  const handleValueChange = useCallback((productRowKey, field, value) => {
    if (field !== 'quantity') return;

    const product = (products || []).find((p) => String(getProductRowKey(p)) === String(productRowKey));
    if (!product?.product?.id) return;

    const productKey = productRowKey;
    const numericValue = value === '' ? null : Math.max(0, Number(value));

    setModifiedProducts(prev => {
      const newState = { ...prev };

      const existingDetail = invoice.invoice_detail.find(d =>
        isSameProductAndFormat(d, product)
      );
      // old_taken es lo que ya estaba tomado antes de esta modificacion
      const oldTaken = existingDetail?.old_taken || product.old_taken || 0;

      if (numericValue === null) {
        const existsInInvoice = invoice.invoice_detail.some(d =>
          isSameProductAndFormat(d, product)
        );
        if (existsInInvoice) {
          newState[productKey] = {
            ...(newState[productKey] || product),
            quantity: 0,
            remaining_quantity: product.originalQuantity - oldTaken,
            taken_quantity: oldTaken,
          };
        } else {
          delete newState[productKey];
        }
        return newState;
      }

      // Calculamos el nuevo remaining basado en:
      // originalQuantity - (oldTaken + nueva cantidad)
      const newRemaining = product.originalQuantity - (oldTaken + numericValue);

      if (newRemaining < 0) return prev; // Validamos que no sea negativo

      newState[productKey] = {
        ...(newState[productKey] || product),
        quantity: numericValue,
        remaining_quantity: newRemaining,
        taken_quantity: oldTaken + numericValue
      };

      return newState;
    });
  }, [products, invoice.invoice_detail, getProductRowKey, isSameProductAndFormat]);

  const shouldEnableSaveButton = useMemo(() => {
    if (loading) return false;
    return (
      Object.values(modifiedProducts).some((item) => Number(item?.quantity) > 0)
      || Object.values(modifiedBonuses).some((item) => Number(item?.quantity) > 0)
      || (invoice.invoice_detail?.length || 0) > 0
      || (invoice.invoice_bonus?.length || 0) > 0
    );
  }, [loading, modifiedProducts, modifiedBonuses, invoice.invoice_detail, invoice.invoice_bonus]);

  const getDisplayValue = (product, field) => {
    if (!product || !product.product) return field === 'quantity' ? '' : 0;

    const productKey = getProductRowKey(product);
    const modified = productKey ? modifiedProducts[productKey] : undefined;
    const invoiceDetail = invoice.invoice_detail.find(d => isSameProductAndFormat(d, product));

    if (field === 'quantity') {
      if (modified?.quantity !== undefined) return modified.quantity;
      if (invoiceDetail) return invoiceDetail.quantity;
      return '';
    }

    return modified?.[field] ?? invoiceDetail?.[field] ?? product[field] ?? '';
  };


  const handlePriceTypeChange = (productRowKey, price) => {
    const product = (products || []).find((p) => String(getProductRowKey(p)) === String(productRowKey));
    if (!product) return;

    const resolvedProductRowKey = getProductRowKey(product);
    if (!resolvedProductRowKey) return;

    setModifiedProducts(prev => {
      // Crear nuevo estado sin mutar el anterior
      const newState = { ...prev };

      // Si no existe entrada para este producto, crear una nueva
      if (!newState[resolvedProductRowKey]) {
        newState[resolvedProductRowKey] = {
          ...product,
          price: price,
          selectedPriceType: getPriceType(product, price),
          // Mantener quantity undefined si no existe
          quantity: prev[resolvedProductRowKey]?.quantity
        };
      } else {
        // Actualizar solo el precio si ya existe
        newState[resolvedProductRowKey] = {
          ...newState[resolvedProductRowKey],
          price: price,
          selectedPriceType: getPriceType(product, price)
        };
      }

      return newState;
    });
  };

  const getPriceType = useCallback((product, price) => {
    if (!product) return 'listPrice';
    if (price === product.bonusPrice && product.bonusPrice > 0) return 'bonusPrice';
    return price === product.listPrice ? 'listPrice' : 'finalPrice';
  }, []);

  const approxTotal = useMemo(() => {
    const total = (products || []).reduce((sum, product) => {
      const productRowKey = getProductRowKey(product);
      if (!productRowKey) return sum;
      const modified = modifiedProducts[productRowKey];
      const invoiceDetail = invoice.invoice_detail.find(d => isSameProductAndFormat(d, product));
      const qty = Number(modified?.quantity ?? invoiceDetail?.quantity ?? 0) || 0;
      const unitPrice = Number(
        modified?.price
        ?? invoiceDetail?.price
        ?? product?.listPrice
        ?? product?.finalPrice
        ?? 0
      ) || 0;
      return sum + (qty * unitPrice);
    }, 0);
    return total;
  }, [products, modifiedProducts, invoice.invoice_detail, getProductRowKey, isSameProductAndFormat]);

  const filteredProducts = useMemo(() => {
    const term = String(searchTerm || '').trim().toLowerCase();
    if (!term) return products || [];
    return (products || []).filter((p) => {
      const code = String(p?.product?.code ?? p?.code ?? '').toLowerCase();
      const description = String(p?.product?.description ?? p?.description ?? '').toLowerCase();
      return code.includes(term) || description.includes(term);
    });
  }, [products, searchTerm]);

  const filteredBonuses = useMemo(() => {
    const term = String(searchTerm || '').trim().toLowerCase();
    if (!term) return bonuses || [];
    return (bonuses || []).filter((b) => {
      const code = String(b?.product?.code ?? b?.code ?? '').toLowerCase();
      const description = String(b?.product?.description ?? b?.description ?? '').toLowerCase();
      return code.includes(term) || description.includes(term);
    });
  }, [bonuses, searchTerm]);

  const handleClean = useCallback(() => {
    setLoading(true);

    // Limpiar estados de forma optimizada
    setModifiedProducts({});
    setModifiedBonuses({});

    // Actualizacion optimizada de invoice
    setInvoice(prev => ({
      ...prev,
      invoice_detail: [],
      invoice_bonus: [],
      total: 0,
      subTotal: 0,
      bonusDiscount: 0,
      priceDiscount: 0
    }));

    // Resetear productos solo si orderUnified existe (evita loops)
    if (orderUnified?.order_details?.length > 0) {
      const resetProducts = orderUnified.order_details.map(item => ({
        ...item,
        quantity: item.quantity,
        taken_quantity: 0,
        remaining_quantity: item.remaining_quantity || 0,
        selectedPriceType: 'listPrice',
        price: item.listPrice
      }));
      setProducts(resetProducts);
    }

    setSnack({ open: true, message: 'Todos los datos han sido limpiados', severity: 'info' });
    setTimeout(() => setOpen(false), 800);
    setLoading(false);
  }, [orderUnified, setOpen]);

  const unifiedDetails = orderUnified?.order_details || orderUnified?.orderDetails || [];
  const hasUnifiedDetails = Array.isArray(unifiedDetails) && unifiedDetails.length > 0;
  const isPreparing = detailLoading || (hasUnifiedDetails && products.length === 0 && bonuses.length === 0);

  if (isPreparing) {
    return (
      <Box sx={{ p: 4 }}>
        <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={36} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={280} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={120} />
      </Box>
    );
  }
  if (!hasUnifiedDetails) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No hay detalles para mostrar.
        </Typography>
      </Box>
    );
  }

  return (

    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(320px, 1fr) auto" },
          alignItems: "end",
          gap: 1,
          mb: 1,
        }}
      >
        {/* Search estilo standard (igual que tu ejemplo bueno) */}
        <TextField
          label="Buscar"
          variant="standard"
          fullWidth
          value={searchTerm}
          size="small"
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Total sutil pero estilizado (sin bloque enorme) */}
        <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
          <Chip
            size="small"
            label={`Total aprox.: ${formatCurrency(approxTotal)}`}
            sx={{
              borderRadius: 1.5,
              fontWeight: 800,
              bgcolor: "action.hover",
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Box>
      </Box>


      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        <TableContainer component={Paper}>
          <Table sx={{ width: '100%' }} size="small">
            <TableHead sx={{ background: "linear-gradient(to right, #10173c, #1e2746)", }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1 }}>Producto</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1 }} align="center">Cant.</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1 }} align="center">Precio</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1 }} align="left">Cant. Disponible</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1 }} align="left">Cant. Tomada</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1 }} align="center">Cant. Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => {
                const productRowKey = getProductRowKey(product);
                if (!productRowKey) return null;
                const invoiceDetailForRow = invoice.invoice_detail.find((d) => isSameProductAndFormat(d, product));
                return (
                <TableRow key={`product-${productRowKey}`} hover>
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap'
                      }}>
                        {/* PRFormat (si existe) */}
                        {product.prformat && (
                          <Chip
                            label={getFormatLabel(product.prformat).toLowerCase()}
                            size="small"
                            color="primary"
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              borderRadius: 1,
                              textTransform: 'capitalize'
                            }}
                          />
                        )}

                        <Stack spacing={2} direction={{ sx: 'column', sm: 'row' }}>
                          <Typography variant="body2" sx={{
                            fontWeight: 500,
                            textTransform: 'capitalize',
                            fontSize: '14px'
                          }}>
                            {String(product.product?.description || '')}
                          </Typography>

                          {/* Unidades */}
                          {product.product.units !== undefined && product.product.units !== null && (
                            <Typography variant="caption" sx={{
                              color: 'text.secondary',
                              ml: 0.5
                            }}>
                              ({typeof product.product.units === 'number'
                                ? product.product.units
                                : String(product.product.units).toLowerCase()})
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <TextField
                        type="text"
                        value={
                          modifiedProducts[productRowKey]?.quantity ??
                          (invoiceDetailForRow?.quantity ?? '')
                        }
                        onChange={(e) => {
                          const value = sanitizeIntegerInput(e.target.value);
                          handleValueChange(productRowKey, 'quantity', value);
                        }}
                        onWheel={handleNumericWheel}
                        size="small"
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          min: 0,
                          step: 1,
                          max:
                            (product?.originalQuantity ?? 0) -
                            ((modifiedProducts[productRowKey]?.taken_quantity ??
                              invoiceDetailForRow?.taken_quantity ??
                              invoiceDetailForRow?.quantity ?? 0))
                        }}

                        sx={{ width: '90px' }}
                        onBlur={(e) => {
                          if (e.target.value === '') {
                            handleValueChange(productRowKey, 'quantity', null);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Select
                        value={
                          modifiedProducts[productRowKey]?.price ??
                          (invoiceDetailForRow?.price ??
                            product?.listPrice) // Siempre listPrice por defecto
                        }
                        onChange={(e) => {
                          if (!isNaN(Number(e.target.value))) {
                            handlePriceTypeChange(productRowKey, Number(e.target.value));
                          }
                        }}
                        size="small"
                        sx={{ width: '150px', fontSize: 14 }}
                      >
                        {product?.finalPrice !== product?.listPrice && (
                          <MenuItem value={product?.finalPrice}>
                            {formatCurrency(product?.finalPrice)} (Final)
                          </MenuItem>
                        )}
                        {product?.bonusPrice > 0 && (
                          <MenuItem value={product?.bonusPrice}>
                            {formatCurrency(product?.bonusPrice)} (Bono)
                          </MenuItem>
                        )}
                        <MenuItem value={product?.listPrice}>
                          {formatCurrency(product?.listPrice)} (Lista)
                        </MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Box
                        sx={{
                          width: '90px',
                          backgroundColor: 'rgba(76, 175, 80, 0.08)',
                          borderRadius: '6px',
                          px: 1,
                          py: 0.5,
                          textAlign: 'right',
                          color: 'text.primary',
                          fontWeight: 'medium',
                          fontSize: '0.95rem'
                        }}
                      >
                        {Number(getDisplayValue(product, 'remaining_quantity')).toLocaleString()}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Box
                        sx={{
                          width: '90px',
                          backgroundColor: 'rgba(33, 150, 243, 0.08)',
                          borderRadius: '6px',
                          px: 1,
                          py: 0.5,
                          textAlign: 'center',
                          color: 'text.primary',
                          fontWeight: 'medium',
                          fontSize: '0.95rem'
                        }}
                      >
                        {Number(getDisplayValue(product, 'taken_quantity')).toLocaleString()}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Box
                        sx={{
                          width: '90px',
                          backgroundColor: 'rgba(158, 158, 158, 0.08)',
                          borderRadius: '6px',
                          px: 1,
                          py: 0.5,
                          textAlign: 'center',
                          color: 'text.primary',
                          fontWeight: 'medium',
                          fontSize: '0.95rem'
                        }}
                      >
                        {Number(product.originalQuantity).toLocaleString()}
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
              {filteredBonuses.map((bonus, idx) => {
                const bonusKey = bonus.bonusKey || buildBonusKey(bonus, idx);
                return (
                  <TableRow
                    key={`bonus-${bonusKey}`}
                    sx={{
                      '& td': {
                        py: '2px',
                        color: 'success.main',
                        fontSize: '0.75rem',
                        borderBottom: '1px dashed rgba(0, 0, 0, 0.12)',
                      },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap'
                      }}>
                        {/* Formato (si existe) */}
                        {bonus.prformat && (
                          <Chip
                            label={getFormatLabel(bonus.prformat).toLowerCase()}
                            size="small"
                            color="success"
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              borderRadius: 1,
                              textTransform: 'capitalize'
                            }}
                          />
                        )}

                        {/* Nombre del producto y unidades */}
                        <Stack
                          spacing={1}
                          direction="row"
                          alignItems="center"
                          sx={{
                            ml: bonus.prformat ? 0 : 1 // Ajuste de margen izquierdo
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              textTransform: 'capitalize',
                              fontSize: '0.9rem'
                            }}
                          >
                            {String(bonus.product?.description || '')}
                          </Typography>

                          {/* Unidades */}
                          {bonus.product?.units !== undefined && bonus.product?.units !== null && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem'
                              }}
                            >
                              ({typeof bonus.product.units === 'number'
                                ? bonus.product.units
                                : String(bonus.product.units).toLowerCase()})
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="text"
                        value={getBonusDisplayValue(bonus, 'quantity')}
                        onChange={(e) => handleBonusChange(bonusKey, idx, 'quantity', sanitizeIntegerInput(e.target.value))}
                        onWheel={handleNumericWheel}
                        size="small"
                        sx={{ width: '80px' }}
                        inputProps={{ min: 0, inputMode: 'numeric', pattern: '[0-9]*' }}
                      />
                    </TableCell>

                    <TableCell></TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {Number(getBonusDisplayValue(bonus, 'remaining_quantity')).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {Number(getBonusDisplayValue(bonus, 'taken_quantity')).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {/* {bonus.originalQuantity} */}
                        {getBonusDisplayValue(bonus, 'originalQuantity')}
                      </Typography>
                    </TableCell>
                  </TableRow>

                )
              })}

            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            color='info'
            startIcon={<CleaningServices />}
            onClick={handleClean}
            disabled={loading}
          >
            Limpiar
          </Button>
          <Button
            variant="outlined"
            color='error'
            startIcon={<Cancel />}
            onClick={() => setOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={!shouldEnableSaveButton}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
          <CustomSnackBar
            open={snack.open}
            setOpen={(val) => setSnack(prev => ({ ...prev, open: val }))}
            message={snack.message}
            severity={snack.severity}
          />
        </Box>
      </Box >
    </>
  )
}




